import React from "react";
import { useI18n } from "@/i18n";
import NewsletterForm from "@/components/NewsletterForm";
import { CheckCircle2, FileText, GraduationCap, Users, Award } from "lucide-react";

const Home = () => {
  const { t, dict } = useI18n();

  const highlights = [
    { key: "support", img: "/images/gov-documents-seal.jpg", icon: FileText },
    { key: "education", img: "/images/desk-paperwork.jpg", icon: GraduationCap },
    { key: "consultation", img: "/images/consultation-office.jpg", icon: Users },
  ];

  return (
    <main className="bg-[#F8FAFC]" data-testid="home-page">
      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#134E4A]/10 text-[#134E4A] text-xs font-semibold tracking-wide uppercase" data-testid="hero-eyebrow">
            <Award className="w-3.5 h-3.5" />
            {t("hero.eyebrow")}
          </span>
          <h1
            className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0B2545] leading-tight"
            data-testid="hero-headline"
          >
            {t("hero.headline")}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#475569] leading-relaxed max-w-2xl mx-auto">
            {t("hero.subheadline")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#newsletter"
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-md bg-[#0B2545] text-white text-sm font-semibold hover:bg-[#0a1f3a]"
              data-testid="hero-cta-primary"
            >
              {t("hero.cta_primary")}
            </a>
            <a
              href="#steps"
              className="inline-flex items-center justify-center px-6 py-3.5 rounded-md border-2 border-[#0B2545] text-[#0B2545] text-sm font-semibold hover:bg-[#0B2545] hover:text-white"
              data-testid="hero-cta-secondary"
            >
              {t("hero.cta_secondary")}
            </a>
          </div>
          <p className="mt-6 text-sm text-[#475569] flex items-center gap-2 justify-center">
            <CheckCircle2 className="w-4 h-4 text-[#134E4A]" />
            {t("hero.trust_line")}
          </p>
        </div>
      </section>

      {/* HEADLINES BLOCK */}
      <section id="apoyos" className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B2545] tracking-tight max-w-3xl">
            {t("headlines_block.title")}
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {(dict.headlines_block.items || []).map((it, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg border border-slate-200 p-6 flex gap-4"
                data-testid={`headline-item-${idx}`}
              >
                <span className="w-9 h-9 rounded-md bg-[#0B2545] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  0{idx + 1}
                </span>
                <p className="text-lg font-medium text-[#0F172A] leading-snug">{it}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section id="educacion" className="pb-16 lg:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B2545] tracking-tight">
              {t("highlights.title")}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#475569] leading-relaxed">
              {t("highlights.subtitle")}
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlights.map(({ key, img, icon: Icon }) => (
              <article key={key} className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col" data-testid={`highlight-${key}`}>
                <img
                  src={img}
                  alt={t(`highlights.items.${key}.image_alt`)}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6 flex-1 flex flex-col">
                  <span className="w-10 h-10 rounded-md bg-[#134E4A]/10 text-[#134E4A] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" />
                  </span>
                  <h3 className="font-serif text-xl font-semibold text-[#0B2545] leading-snug">
                    {t(`highlights.items.${key}.title`)}
                  </h3>
                  <p className="mt-3 text-sm text-[#475569] leading-relaxed">
                    {t(`highlights.items.${key}.body`)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section id="consulta" className="py-16 lg:py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <img
              src="/images/executive-advisor.jpg"
              alt={t("expert.image_alt")}
              className="w-full h-[440px] object-cover rounded-lg border border-slate-200"
            />
            <p className="mt-4 text-sm text-[#475569] italic">{t("expert.highlight")}</p>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B2545] tracking-tight">
              {t("checklist.title")}
            </h2>
            <p className="mt-4 text-base text-[#475569] leading-relaxed">{t("checklist.subtitle")}</p>
            <ul className="mt-6 space-y-3" data-testid="qualification-checklist">
              {(dict.checklist.items || []).map((it, idx) => (
                <li key={idx} className="flex gap-3 items-start bg-[#F8FAFC] rounded-md p-4 border border-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-[#134E4A] shrink-0 mt-0.5" />
                  <span className="text-[#0F172A]">{it}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-[#475569]">{t("checklist.footnote")}</p>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section id="steps" className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B2545] tracking-tight max-w-3xl">
            {t("steps.title")}
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {(dict.steps.items || []).map((it, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-slate-200 p-6" data-testid={`step-${idx}`}>
                <span className="font-serif text-4xl font-bold text-[#D97706]">{it.n}</span>
                <h3 className="mt-3 font-serif text-xl font-semibold text-[#0B2545]">{it.title}</h3>
                <p className="mt-2 text-sm text-[#475569] leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER + EXPERT SIDE */}
      <section id="newsletter" className="py-16 lg:py-20 bg-[#0B2545] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-6">
            <span className="inline-block px-3 py-1 rounded-full bg-white/10 text-[#D97706] text-xs font-semibold tracking-wide uppercase">
              {t("newsletter.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight">
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-base text-slate-300 leading-relaxed max-w-xl">
              {t("newsletter.subtitle")}
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              {(dict.headlines_block.items || []).slice(0, 3).map((it, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#D97706] shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <NewsletterForm />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B2545] tracking-tight">
            {t("testimonials.title")}
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {(dict.testimonials.items || []).map((it, idx) => (
              <figure key={idx} className="bg-white rounded-lg border border-slate-200 p-6" data-testid={`testimonial-${idx}`}>
                <blockquote className="text-[#0F172A] leading-relaxed">"{it.quote}"</blockquote>
                <figcaption className="mt-5 pt-4 border-t border-slate-200">
                  <p className="font-semibold text-[#0B2545]">{it.name}</p>
                  <p className="text-xs text-[#475569] mt-0.5">{it.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="contacto" className="pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#0B2545] tracking-tight">
            {t("faq.title")}
          </h2>
          <div className="mt-8 space-y-4">
            {(dict.faq.items || []).map((it, idx) => (
              <details
                key={idx}
                className="bg-white rounded-lg border border-slate-200 p-5 group"
                data-testid={`faq-${idx}`}
              >
                <summary className="font-semibold text-[#0B2545] cursor-pointer list-none flex items-center justify-between gap-4">
                  <span>{it.q}</span>
                  <span className="text-[#D97706] text-xl leading-none group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-[#475569] leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
